import { useEffect, useRef, useState, type FormEvent } from "react";
import type {
  NumberPurchaseOperation,
  NumberPurchasePolicy,
  NumberPurchasePrice,
  NumberPurchaseQuote,
  NumberPurchaseSearch,
} from "@voice/api-contract";

export type NumberPurchaseApi = <T>(
  path: string,
  options?: {
    method?: string;
    body?: unknown;
    csrf?: string | undefined;
    signal?: AbortSignal;
  },
) => Promise<T>;
export type PurchaseLocation = {
  id: string;
  name: string;
  version?: number | string;
  address?: {
    line1?: string;
    line2?: string;
    city?: string;
    region?: string;
    postalCode?: string;
  };
};
type Props = {
  api: NumberPurchaseApi;
  csrf?: string | undefined;
  accountId: string;
  accountName: string;
  locations: PurchaseLocation[];
  customer: boolean;
  readOnly?: boolean | undefined;
  preferredNumber?: string | undefined;
  onSaved: () => Promise<void>;
  onSetup?: (() => void) | undefined;
};
const uuid =
  /^[a-f0-9]{8}-[a-f0-9]{4}-[1-5][a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/i;
const e164 = /^\+1[2-9]\d{2}[2-9]\d{6}$/;
const dollars = (minor: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
    minor / 100,
  );
const stamp = (value: string) => new Date(value).toLocaleString();
const pending = (operation: NumberPurchaseOperation) =>
  ["submitting", "unknown"].includes(operation.status);
const validPrice = (price: NumberPurchasePrice) =>
  price &&
  price.currency === "USD" &&
  price.setupMinor === 0 &&
  Number.isSafeInteger(price.monthlyMinor) &&
  price.monthlyMinor >= 0 &&
  ["included", "additional", "complimentary"].includes(price.reason) &&
  (price.reason === "additional" || price.monthlyMinor === 0);
const validOperation = (operation: NumberPurchaseOperation) =>
  operation &&
  uuid.test(operation.id) &&
  uuid.test(operation.requestKey) &&
  e164.test(operation.number) &&
  ["submitting", "unknown", "purchased", "failed"].includes(operation.status) &&
  typeof operation.detail === "string" &&
  operation.safeToRetry === false &&
  validPrice(operation.customerPrice) &&
  Number.isFinite(Date.parse(operation.updatedAt));
function storedKey(name: string) {
  try {
    const value = sessionStorage.getItem(name);
    return value && uuid.test(value) ? value : null;
  } catch {
    return null;
  }
}
function remember(name: string, value: string | null) {
  try {
    if (value) sessionStorage.setItem(name, value);
    else sessionStorage.removeItem(name);
  } catch {
    /* The server's account history also retains unresolved purchases. */
  }
}
async function bounded<T>(
  api: NumberPurchaseApi,
  path: string,
  signal: AbortSignal,
  options: Parameters<NumberPurchaseApi>[1] = {},
) {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  let cancel = () => {};
  const interrupted = new Promise<never>((_resolve, reject) => {
    cancel = () => {
      controller.abort();
      reject(new Error("Request canceled."));
    };
    if (signal.aborted) cancel();
    else {
      signal.addEventListener("abort", cancel, { once: true });
      timer = setTimeout(
        () => {
          controller.abort();
          reject(new Error("The request timed out."));
        },
        options.method === "POST" ? 30_000 : 10_000,
      );
    }
  });
  try {
    return await Promise.race([
      Promise.resolve().then(() => {
        if (controller.signal.aborted) throw new Error("Request canceled.");
        return api<T>(path, { ...options, signal: controller.signal });
      }),
      interrupted,
    ]);
  } finally {
    clearTimeout(timer);
    signal.removeEventListener("abort", cancel);
  }
}

export function NumberPurchasePanel(props: Props) {
  if (props.readOnly)
    return (
      <p className="cr-form-note">
        Read-only customer view. Return to staff access to buy a number.
      </p>
    );
  return (
    <PurchaseFlow key={`${props.customer}:${props.accountId}`} {...props} />
  );
}

function PurchaseFlow({
  api,
  csrf,
  accountId,
  accountName,
  locations,
  customer,
  onSaved,
  onSetup,
  preferredNumber,
}: Props) {
  const base = `/${customer ? "customer" : "admin"}/accounts/${accountId}/number-purchases`;
  const storage = `cr-number-purchase:${customer ? "customer" : "admin"}:${accountId}`;
  const lifecycle = useRef(new AbortController());
  const submitting = useRef(false);
  const [locationId, setLocationId] = useState(
    locations.length === 1 ? locations[0]!.id : "",
  );
  const [mode, setMode] = useState("address");
  const [area, setArea] = useState("");
  const [search, setSearch] = useState<NumberPurchaseSearch | null>(null);
  const [quote, setQuote] = useState<NumberPurchaseQuote | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [policy, setPolicy] = useState<NumberPurchasePolicy | null>(null);
  const [history, setHistory] = useState<NumberPurchaseOperation[]>([]);
  const [recoveryKey, setRecoveryKey] = useState<string | null>(() =>
    storedKey(storage),
  );
  const [historyReady, setHistoryReady] = useState(false);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [revision, setRevision] = useState(0);
  const [now, setNow] = useState(Date.now());
  const selection = useRef(0);
  const selectedLocation = locations.find((item) => item.id === locationId);
  const unresolved = history.filter(pending);
  const blocked =
    !historyReady || Boolean(recoveryKey) || unresolved.length > 0;
  const expired = quote ? now >= Date.parse(quote.expiresAt) : false;

  useEffect(() => {
    const current = new AbortController();
    lifecycle.current = current;
    setHistoryReady(false);
    setError("");
    void bounded<{
      items: NumberPurchaseOperation[];
      policy: NumberPurchasePolicy;
    }>(api, base, current.signal)
      .then((result) => {
        if (current.signal.aborted) return;
        if (
          !Array.isArray(result.items) ||
          !result.items.every(validOperation) ||
          result.policy?.configured !== true ||
          typeof result.policy.enabled !== "boolean"
        )
          throw new Error("Purchase history could not be verified.");
        setHistory(result.items);
        setPolicy(result.policy);
        const saved = storedKey(storage);
        const found = result.items.find((item) => item.requestKey === saved);
        if (found && !pending(found)) {
          remember(storage, null);
          setRecoveryKey(null);
        } else setRecoveryKey(saved);
        setHistoryReady(true);
      })
      .catch((cause) => {
        if (!current.signal.aborted)
          setError(
            `${(cause as Error).message} Reload purchase status before buying.`,
          );
      });
    return () => current.abort();
  }, [api, base, revision, storage]);
  useEffect(() => {
    if (!quote) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [quote]);
  useEffect(() => {
    if (
      quote &&
      selectedLocation?.version !== undefined &&
      Number(selectedLocation.version) !== quote.locationVersion
    ) {
      setQuote(null);
      setAcknowledged(false);
      setError(
        "The service address changed. Review a new quote before buying.",
      );
    }
  }, [quote, selectedLocation?.version]);
  function resetSelection() {
    selection.current += 1;
    setQuote(null);
    setSearch(null);
    setAcknowledged(false);
    setError("");
    setNotice("");
  }
  async function find(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy || blocked || !selectedLocation) return;
    const generation = ++selection.current;
    const current = lifecycle.current;
    setBusy("search");
    setError("");
    setQuote(null);
    setAcknowledged(false);
    setSearch(null);
    try {
      const query = new URLSearchParams({ locationId });
      if (mode === "area") query.set("areaCode", area);
      const result = await bounded<NumberPurchaseSearch>(
        api,
        `${base}/search?${query}`,
        current.signal,
      );
      if (current.signal.aborted || generation !== selection.current) return;
      if (
        result.locationId !== locationId ||
        !Array.isArray(result.items) ||
        !result.items.every((row) => e164.test(row.number))
      )
        throw new Error("Number availability could not be verified.");
      setSearch(result);
    } catch (cause) {
      if (!current.signal.aborted && generation === selection.current)
        setError((cause as Error).message);
    } finally {
      if (!current.signal.aborted && generation === selection.current)
        setBusy("");
    }
  }
  async function review(number: string) {
    if (busy || blocked || !selectedLocation || !policy?.enabled) return;
    const generation = ++selection.current;
    const current = lifecycle.current;
    setBusy("quote");
    setQuote(null);
    setAcknowledged(false);
    setError("");
    setNotice("");
    try {
      const result = await bounded<NumberPurchaseQuote>(
        api,
        `${base}/quotes`,
        current.signal,
        { method: "POST", csrf, body: { number, locationId } },
      );
      if (current.signal.aborted || generation !== selection.current) return;
      if (
        result.accountId !== accountId ||
        result.number !== number ||
        result.locationId !== locationId ||
        !uuid.test(result.id) ||
        !validPrice(result.customerPrice) ||
        !Number.isFinite(Date.parse(result.expiresAt)) ||
        !result.address?.line1 ||
        !result.address.city ||
        !result.address.region ||
        !result.address.postalCode ||
        result.capabilities?.voice !== true
      )
        throw new Error(
          "The number, address or price in this quote could not be verified. Request a new quote.",
        );
      setQuote(result);
      setNow(Date.now());
    } catch (cause) {
      if (!current.signal.aborted && generation === selection.current)
        setError((cause as Error).message);
    } finally {
      if (!current.signal.aborted && generation === selection.current)
        setBusy("");
    }
  }
  async function acceptOperation(operation: NumberPurchaseOperation) {
    if (!validOperation(operation))
      throw new Error("The purchase response could not be verified.");
    setHistory((prior) => [
      operation,
      ...prior.filter((item) => item.requestKey !== operation.requestKey),
    ]);
    if (pending(operation)) {
      remember(storage, operation.requestKey);
      setRecoveryKey(operation.requestKey);
      return;
    }
    remember(storage, null);
    setRecoveryKey(null);
    setQuote(null);
    setAcknowledged(false);
    if (operation.status === "purchased") {
      setSearch(null);
      setNotice(
        "Number purchased and linked to this account. Routing, equipment and E911 setup are still required. No payment was collected by this purchase.",
      );
      try {
        await onSaved();
      } catch {
        setError(
          "The number was purchased. Account records could not refresh; reload the account before making another purchase.",
        );
      }
    }
  }
  async function checkStatus(key: string) {
    if (busy || submitting.current) return;
    const current = lifecycle.current;
    setBusy("status");
    setError("");
    try {
      const result = await bounded<{ operation: NumberPurchaseOperation }>(
        api,
        `${base}/purchases/${key}`,
        current.signal,
      );
      if (current.signal.aborted) return;
      if (result.operation?.requestKey !== key)
        throw new Error("The purchase status did not match this request.");
      const expectedNumber =
        history.find((item) => item.requestKey === key)?.number ??
        (recoveryKey === key ? quote?.number : undefined);
      if (expectedNumber && result.operation.number !== expectedNumber)
        throw new Error(
          "The purchase status did not match this telephone number.",
        );
      await acceptOperation(result.operation);
    } catch {
      if (!current.signal.aborted)
        setError(
          "Purchase status could not be confirmed. Do not submit another purchase. Check status again or contact Cache River with the request reference below.",
        );
    } finally {
      if (!current.signal.aborted) setBusy("");
    }
  }
  async function buy(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (
      submitting.current ||
      busy ||
      blocked ||
      !quote ||
      !acknowledged ||
      !policy?.enabled ||
      Date.now() >= Date.parse(quote.expiresAt)
    )
      return;
    submitting.current = true;
    const current = lifecycle.current;
    const requestKey = crypto.randomUUID();
    remember(storage, requestKey);
    setRecoveryKey(requestKey);
    setBusy("purchase");
    setError("");
    try {
      const result = await bounded<{ operation: NumberPurchaseOperation }>(
        api,
        `${base}/purchases`,
        current.signal,
        {
          method: "POST",
          csrf,
          body: { quoteId: quote.id, requestKey, acknowledged: true },
        },
      );
      if (current.signal.aborted) return;
      if (
        result.operation?.requestKey !== requestKey ||
        result.operation.number !== quote.number
      )
        throw new Error("Purchase response mismatch.");
      await acceptOperation(result.operation);
    } catch (cause) {
      if (current.signal.aborted) return;
      const status = (cause as Error & { status?: number }).status;
      if (
        status !== undefined &&
        [400, 403, 404, 409, 424, 429].includes(status)
      ) {
        try {
          const result = await bounded<{ operation: NumberPurchaseOperation }>(
            api,
            `${base}/purchases/${requestKey}`,
            current.signal,
          );
          if (current.signal.aborted) return;
          if (result.operation?.requestKey !== requestKey)
            throw new Error("Purchase response mismatch.");
          await acceptOperation(result.operation);
          return;
        } catch (lookup) {
          if (current.signal.aborted) return;
          if ((lookup as Error & { status?: number }).status === 404) {
            remember(storage, null);
            setRecoveryKey(null);
            setQuote(null);
            setAcknowledged(false);
            setHistoryReady(false);
            setRevision((value) => value + 1);
            setNotice(
              `The purchase was not submitted. ${(cause as Error).message} Review a fresh quote before trying again.`,
            );
            return;
          }
        }
      }
      setError(
        "The purchase response was interrupted. The number may have been purchased. Check purchase status before doing anything else; this request will not be sent again.",
      );
    } finally {
      submitting.current = false;
      if (!current.signal.aborted) setBusy("");
    }
  }
  return (
    <section
      className="cr-panel cr-number-purchase"
      aria-label="Buy a new telephone number"
    >
      <h3>Buy a new number</h3>
      <p>
        The first number is included with the monthly plan. Additional numbers
        use Twilio’s current monthly cost, with no markup or setup fee.
        Complimentary accounts pay $0.
      </p>
      {!historyReady && !error && (
        <p role="status">Checking prior purchases…</p>
      )}
      {error && (
        <p className="cr-notice error" role="alert">
          {error}
        </p>
      )}
      {notice && (
        <p className="cr-notice" role="status">
          {notice}
        </p>
      )}
      {!historyReady && (
        <button
          className="cr-button secondary"
          disabled={Boolean(busy)}
          onClick={() => setRevision((value) => value + 1)}
        >
          Reload purchase status
        </button>
      )}
      {policy?.enabled === false && (
        <p className="cr-notice">
          New number purchases are paused. Existing numbers keep their current
          service.
        </p>
      )}
      {(recoveryKey || unresolved.length > 0) && (
        <div className="cr-notice" role="status">
          <p>
            A purchase is awaiting confirmation. Another purchase is unavailable
            until its status is known.
          </p>
          {[
            ...new Set([
              ...unresolved.map((item) => item.requestKey),
              ...(recoveryKey ? [recoveryKey] : []),
            ]),
          ].map((key) => (
            <div key={key}>
              <p>Request reference: {key}</p>
              <button
                type="button"
                className="cr-button secondary"
                disabled={Boolean(busy)}
                onClick={() => void checkStatus(key)}
              >
                {busy === "status"
                  ? "Checking purchase…"
                  : "Check purchase status"}
              </button>
            </div>
          ))}
        </div>
      )}
      {locations.length === 0 ? (
        <p>Add a service location to this account before buying a number.</p>
      ) : (
        <>
          {preferredNumber &&
            e164.test(preferredNumber) &&
            !quote &&
            !history.some(
              (item) =>
                item.status === "purchased" && item.number === preferredNumber,
            ) && (
              <p>
                Requested number: {preferredNumber}.{" "}
                <button
                  type="button"
                  className="cr-button secondary"
                  disabled={
                    !selectedLocation ||
                    Boolean(busy) ||
                    blocked ||
                    !policy?.enabled
                  }
                  onClick={() => void review(preferredNumber)}
                >
                  Review requested number
                </button>
              </p>
            )}
          <form className="cr-form" onSubmit={find}>
            <fieldset disabled={Boolean(busy) || blocked || !policy?.enabled}>
              <legend>Find a number for {accountName}</legend>
              <label className="cr-field">
                <span>Service location</span>
                <select
                  value={locationId}
                  required
                  onChange={(event) => {
                    resetSelection();
                    setLocationId(event.target.value);
                  }}
                >
                  <option value="">Choose a service location</option>
                  {locations.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                      {item.address?.city
                        ? ` · ${item.address.city}, ${item.address.region ?? ""}`
                        : ""}
                    </option>
                  ))}
                </select>
              </label>
              <label className="cr-field">
                <span>Where should the number be local?</span>
                <select
                  value={mode}
                  onChange={(event) => {
                    resetSelection();
                    setMode(event.target.value);
                  }}
                >
                  <option value="address">
                    Near the selected service address
                  </option>
                  <option value="area">Another area code</option>
                </select>
              </label>
              {mode === "area" && (
                <label className="cr-field">
                  <span>Area code</span>
                  <input
                    value={area}
                    onChange={(event) => {
                      resetSelection();
                      setArea(
                        event.target.value.replace(/\D/g, "").slice(0, 3),
                      );
                    }}
                    required
                    inputMode="numeric"
                    pattern="[2-9][0-9]{2}"
                    minLength={3}
                    maxLength={3}
                  />
                </label>
              )}
              <button
                className="cr-button secondary"
                disabled={!selectedLocation}
              >
                {busy === "search" ? "Searching Twilio…" : "Search numbers"}
              </button>
            </fieldset>
          </form>
          {busy === "quote" && (
            <p role="status">Checking availability and the current price…</p>
          )}
          {search && !quote && (
            <>
              <p>
                Availability checked {stamp(search.checkedAt)}. A search does
                not hold a number.
              </p>
              {search.items.length ? (
                <div
                  className="cr-table-wrap"
                  role="region"
                  aria-label="New numbers available to purchase"
                  tabIndex={0}
                >
                  <table>
                    <thead>
                      <tr>
                        <th>Telephone number</th>
                        <th>Local area</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {search.items.map((row) => (
                        <tr key={row.number}>
                          <td>{row.number}</td>
                          <td>{row.region || "Not supplied"}</td>
                          <td>
                            <button
                              type="button"
                              className="cr-button secondary"
                              disabled={
                                Boolean(busy) || blocked || !policy?.enabled
                              }
                              onClick={() => void review(row.number)}
                            >
                              Review {row.number}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p>
                  No matching numbers are available. Search another area code.
                </p>
              )}
            </>
          )}
          {quote && (
            <form
              className="cr-form"
              onSubmit={buy}
              aria-label="Confirm number purchase"
            >
              <h4>Review this purchase</h4>
              <dl className="cr-details">
                <div>
                  <dt>Customer</dt>
                  <dd>{accountName}</dd>
                </div>
                <div>
                  <dt>Telephone number</dt>
                  <dd>{quote.number}</dd>
                </div>
                <div>
                  <dt>Service address</dt>
                  <dd>
                    {quote.locationName}
                    <br />
                    {[
                      quote.address.line1,
                      quote.address.line2,
                      quote.address.city,
                      quote.address.region,
                      quote.address.postalCode,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  </dd>
                </div>
                <div>
                  <dt>Setup charge</dt>
                  <dd>{dollars(quote.customerPrice.setupMinor)}</dd>
                </div>
                <div>
                  <dt>Monthly number charge</dt>
                  <dd>
                    {dollars(quote.customerPrice.monthlyMinor)}
                    {quote.customerPrice.reason === "included"
                      ? " · First number included with the monthly plan"
                      : quote.customerPrice.reason === "complimentary"
                        ? " · Complimentary account"
                        : " · Current Twilio cost, no markup"}
                  </dd>
                </div>
                {!customer && quote.carrierPrice && (
                  <div>
                    <dt>Carrier monthly cost</dt>
                    <dd>
                      {quote.carrierPrice.currency} {quote.carrierPrice.monthly}{" "}
                      · checked {stamp(quote.carrierPrice.checkedAt)}
                    </dd>
                  </div>
                )}
              </dl>
              <p>
                Voice number.{" "}
                {quote.capabilities.sms || quote.capabilities.mms
                  ? "Texting needs separate service approval. "
                  : ""}
                Buying links the number to this account. It does not configure
                ringing, equipment or emergency calling. Monthly number charges
                do not include call usage or applicable taxes.
              </p>
              <p>Quote expires {stamp(quote.expiresAt)}.</p>
              {expired && (
                <p className="cr-notice" role="status">
                  This quote has expired. Refresh the quote before buying.
                </p>
              )}
              <label className="cr-check">
                <input
                  type="checkbox"
                  checked={acknowledged}
                  disabled={Boolean(busy) || blocked || expired}
                  onChange={(event) => setAcknowledged(event.target.checked)}
                />
                <span>
                  I authorize the purchase of {quote.number} for this account at
                  the charges shown.
                </span>
              </label>
              <div className="cr-actions">
                <button
                  className="cr-button"
                  disabled={
                    !acknowledged ||
                    Boolean(busy) ||
                    blocked ||
                    expired ||
                    !policy?.enabled
                  }
                >
                  {busy === "purchase"
                    ? "Purchasing number…"
                    : `Buy ${quote.number} · ${dollars(quote.customerPrice.monthlyMinor)}/month`}
                </button>
                <button
                  type="button"
                  className="cr-button secondary"
                  disabled={Boolean(busy) || blocked}
                  onClick={() => void review(quote.number)}
                >
                  Refresh quote
                </button>
                <button
                  type="button"
                  className="cr-text-button"
                  disabled={Boolean(busy) || blocked}
                  onClick={() => {
                    setQuote(null);
                    setAcknowledged(false);
                  }}
                >
                  Choose another number
                </button>
              </div>
            </form>
          )}
        </>
      )}
      {history.length > 0 && (
        <div>
          <h4>Recent number purchases</h4>
          {history.slice(0, 10).map((item) => (
            <div key={item.id}>
              <p>
                <strong>{item.number}</strong> ·{" "}
                {item.status === "purchased"
                  ? "Purchased, setup required"
                  : item.status === "failed"
                    ? "Not purchased"
                    : "Awaiting confirmation"}
                <br />
                {item.detail}
                <br />
                <small>
                  {stamp(item.updatedAt)} · Request {item.requestKey}
                </small>
              </p>
              {item.status === "purchased" && onSetup && (
                <button
                  type="button"
                  className="cr-button secondary"
                  onClick={onSetup}
                >
                  Continue number setup
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export function AdminNumberPurchasePanel({
  api,
  csrf,
  accounts,
  onSaved,
  onCustomers,
  onAccount,
}: {
  api: NumberPurchaseApi;
  csrf?: string | undefined;
  accounts: { id: string; name: string; account_number?: string }[];
  onSaved: () => Promise<void>;
  onCustomers: () => void;
  onAccount: (id: string) => void;
}) {
  const [accountId, setAccountId] = useState("");
  const [workspace, setWorkspace] = useState<{
    account: { id: string; name: string };
    locations: PurchaseLocation[];
  } | null>(null);
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  const [policyRevision, setPolicyRevision] = useState(0);
  useEffect(() => {
    setWorkspace(null);
    setError("");
    if (!accountId) return;
    const controller = new AbortController();
    void bounded<{
      account: { id: string; name: string };
      locations: PurchaseLocation[];
    }>(api, `/admin/accounts/${accountId}`, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        if (
          result.account?.id !== accountId ||
          !Array.isArray(result.locations)
        )
          throw new Error("Customer details could not be verified.");
        setWorkspace(result);
      })
      .catch((cause) => {
        if (!controller.signal.aborted) setError((cause as Error).message);
      });
    return () => controller.abort();
  }, [api, accountId, revision]);
  return (
    <>
      <NumberPurchaseSettings
        api={api}
        csrf={csrf}
        onChanged={() => setPolicyRevision((value) => value + 1)}
      />
      <section className="cr-panel">
        <h2>Buy a new number</h2>
        <p>
          Choose the customer first. You will review the number, service address
          and price before buying.
        </p>
        <label className="cr-field">
          <span>Customer</span>
          <select
            value={accountId}
            onChange={(event) => {
              setWorkspace(null);
              setAccountId(event.target.value);
            }}
          >
            <option value="">Choose a customer</option>
            {accounts.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
                {item.account_number ? ` · ${item.account_number}` : ""}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="cr-text-button" onClick={onCustomers}>
          Open customers to find or add an account
        </button>
        {accountId && !workspace && !error && (
          <p role="status">Loading the customer’s service locations…</p>
        )}
        {error && (
          <p className="cr-notice error" role="alert">
            {error}{" "}
            <button
              type="button"
              className="cr-text-button"
              onClick={() => setRevision((value) => value + 1)}
            >
              Reload customer
            </button>
          </p>
        )}
      </section>
      {workspace && (
        <NumberPurchasePanel
          key={`${workspace.account.id}:${policyRevision}`}
          api={api}
          csrf={csrf}
          accountId={workspace.account.id}
          accountName={workspace.account.name}
          locations={workspace.locations}
          customer={false}
          onSaved={onSaved}
          onSetup={() => onAccount(workspace.account.id)}
        />
      )}
    </>
  );
}

export function NumberPurchaseSettings({
  api,
  csrf,
  onChanged,
}: {
  api: NumberPurchaseApi;
  csrf?: string | undefined;
  onChanged?: (() => void) | undefined;
}) {
  const path = "/admin/number-purchase-settings";
  const lifecycle = useRef(new AbortController());
  const [policy, setPolicy] = useState<NumberPurchasePolicy | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    const current = new AbortController();
    lifecycle.current = current;
    setPolicy(null);
    setError("");
    setNotice("");
    void bounded<NumberPurchasePolicy>(api, path, current.signal)
      .then((result) => {
        if (current.signal.aborted) return;
        if (
          !Number.isSafeInteger(result.version) ||
          typeof result.enabled !== "boolean" ||
          result.firstNumberIncluded !== true ||
          result.additionalAtCarrierCost !== true
        )
          throw new Error("Number purchase settings could not be verified.");
        setPolicy(result);
        setEnabled(result.enabled);
      })
      .catch((cause) => {
        if (!current.signal.aborted) setError((cause as Error).message);
      });
    return () => current.abort();
  }, [api, revision]);
  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!policy || busy || enabled === policy.enabled) return;
    const current = lifecycle.current;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const result = await bounded<NumberPurchasePolicy>(
        api,
        path,
        current.signal,
        {
          method: "PUT",
          csrf,
          body: { expectedVersion: policy.version, enabled },
        },
      );
      if (current.signal.aborted) return;
      if (
        !Number.isSafeInteger(result.version) ||
        result.enabled !== enabled ||
        result.firstNumberIncluded !== true ||
        result.additionalAtCarrierCost !== true
      )
        throw new Error("Settings response could not be verified.");
      setPolicy(result);
      setEnabled(result.enabled);
      setNotice(
        result.enabled
          ? "New number purchasing is enabled."
          : "New number purchasing is paused.",
      );
      onChanged?.();
    } catch {
      if (!current.signal.aborted)
        setError(
          "Settings may have changed. Reload the current settings before saving again.",
        );
    } finally {
      if (!current.signal.aborted) setBusy(false);
    }
  }
  return (
    <section
      className="cr-panel cr-number-purchase-settings"
      aria-label="Number purchase settings"
    >
      <h3>Number purchase settings</h3>
      <p>
        First number: included with the monthly plan. Additional numbers:
        current Twilio monthly cost, with no markup or setup fee. Complimentary
        accounts: $0.
      </p>
      {!policy && !error && <p role="status">Loading purchase settings…</p>}
      {error && (
        <p className="cr-notice error" role="alert">
          {error}
        </p>
      )}
      {notice && (
        <p className="cr-notice" role="status">
          {notice}
        </p>
      )}
      <form className="cr-form" onSubmit={save}>
        <label className="cr-check">
          <input
            type="checkbox"
            checked={enabled}
            disabled={!policy || busy || Boolean(error)}
            onChange={(event) => setEnabled(event.target.checked)}
          />
          <span>Allow staff and reviewed customers to buy new numbers</span>
        </label>
        <button
          className="cr-button secondary"
          disabled={
            !policy || busy || Boolean(error) || enabled === policy.enabled
          }
        >
          {busy ? "Saving purchase setting…" : "Save purchase setting"}
        </button>
      </form>
      <p>Pausing purchases does not interrupt existing phone service.</p>
      <button
        type="button"
        className="cr-text-button"
        disabled={busy}
        onClick={() => {
          setRevision((value) => value + 1);
          onChanged?.();
        }}
      >
        Reload current settings
      </button>
    </section>
  );
}
